import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { documentApi } from '../../api/endpoints.js';
import { useApi } from '../../hooks/useApi.js';
import PageHeader from '../../components/PageHeader.jsx';
import Toolbar from '../../components/Toolbar.jsx';
import DataTable from '../../components/DataTable.jsx';
import Badge from '../../components/Badge.jsx';
import StatCard from '../../components/StatCard.jsx';
import { Select, TextInput } from '../../components/Field.jsx';
import { formatCurrency, formatDate } from '../../utils/format.js';
import { DOCUMENT_TYPES, DOCUMENT_STATUSES } from '../../constants.js';

/** Quotes, invoices, receipts and credit notes - one list, filtered by type. */
const DocumentListPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.resolvedLanguage;

  const [filters, setFilters] = useState({ type: '', status: '', q: '' });
  const { data, loading } = useApi(() => documentApi.list({ ...filters, pageSize: 200 }), [filters]);

  const rows = data?.items ?? [];
  const invoices = rows.filter((row) => row.type === 'INVOICE');
  const outstanding = invoices
    .filter((row) => !['PAID', 'CANCELLED'].includes(row.status))
    .reduce((sum, row) => sum + (row.balance ?? 0), 0);
  const collected = invoices.reduce((sum, row) => sum + (Number(row.paidAmount) || 0), 0);

  const columns = [
    {
      key: 'number',
      header: t('documents.number'),
      render: (row) => (
        <div className="cell-stack">
          <span className="cell-strong">{row.number}</span>
          <Badge value={row.type} label={t(`documents.types.${row.type}`)} />
        </div>
      ),
    },
    {
      key: 'customer',
      header: t('customers.companyName'),
      render: (row) => row.customerName ?? '-',
    },
    { key: 'issueDate', header: t('documents.issueDate'), render: (row) => formatDate(row.issueDate, locale) },
    {
      key: 'total',
      header: t('documents.total'),
      className: 'cell-right',
      render: (row) => formatCurrency(row.total, locale, row.currency),
    },
    {
      key: 'balance',
      header: t('documents.balance'),
      className: 'cell-right',
      render: (row) =>
        row.type === 'INVOICE' ? formatCurrency(row.balance, locale, row.currency) : '-',
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (row) => <Badge value={row.status} label={t(`documents.statuses.${row.status}`, row.status)} />,
    },
  ];

  return (
    <>
      <PageHeader
        title={t('documents.title')}
        subtitle={t('documents.subtitle')}
        actions={
          <button type="button" className="btn btn--primary" onClick={() => navigate('/documents/new?type=QUOTE')}>
            {t('documents.newQuote')}
          </button>
        }
      />

      <div className="stat-grid">
        <StatCard label={t('documents.countAll')} value={rows.length} />
        <StatCard label={t('documents.countInvoices')} value={invoices.length} />
        <StatCard label={t('documents.collected')} value={formatCurrency(collected, locale)} tone="success" />
        <StatCard label={t('documents.outstanding')} value={formatCurrency(outstanding, locale)} tone="danger" />
      </div>

      <Toolbar>
        <TextInput value={filters.q} onChange={(v) => setFilters({ ...filters, q: v })} placeholder={t('common.search')} />
        <Select
          value={filters.type}
          onChange={(v) => setFilters({ ...filters, type: v })}
          placeholder={t('common.all')}
          options={DOCUMENT_TYPES.map((type) => ({ value: type, label: t(`documents.types.${type}`) }))}
        />
        <Select
          value={filters.status}
          onChange={(v) => setFilters({ ...filters, status: v })}
          placeholder={t('common.all')}
          options={DOCUMENT_STATUSES.map((status) => ({ value: status, label: t(`documents.statuses.${status}`) }))}
        />
        <span className="toolbar__count">
          {rows.length} {t('common.results')}
        </span>
      </Toolbar>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        emptyTitle={t('documents.empty')}
        onRowClick={(row) => navigate(`/documents/${row.id}`)}
      />
    </>
  );
};

export default DocumentListPage;
